import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import analyticsService from '../services/analyticsService';
import { setAnalyticsDemoActive } from './analyticsDemo';
import { TOUR_STEPS, TUTORIAL_SEEN_KEY, type TourStep } from './tourSteps';

interface TutorialContextValue {
  active: boolean;
  stepIndex: number;
  steps: TourStep[];
  start: () => void;
  stop: (completed?: boolean) => void;
  next: () => void;
  back: () => void;
  /** True once the user has completed or dismissed the tour on this device. */
  hasSeen: boolean;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

function readSeen(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSeen(): void {
  try {
    localStorage.setItem(TUTORIAL_SEEN_KEY, 'true');
  } catch {
    /* ignore storage failures (private mode, etc.) */
  }
}

/**
 * Decide whether the analytics pages should show curated demo data during the
 * tour. Rule (per product): use demo data when the tenant has less than roughly
 * a month of real hazard activity; otherwise show their real analytics.
 */
async function probeShouldUseDemo(): Promise<boolean> {
  try {
    const real = await analyticsService.getReportSummary();
    const reported = real.headline?.reported ?? 0;
    const end = real.period?.end ? new Date(real.period.end).getTime() : Date.now();
    const start = real.period?.start ? new Date(real.period.start).getTime() : end;
    const spanDays = (end - start) / 86_400_000;
    // Sparse tenant: little history or very little data → show demo.
    return reported < 30 || spanDays < 30;
  } catch {
    // If we can't tell (e.g., endpoint unavailable), prefer the always-populated
    // demo so the tour never shows an empty analytics page.
    return true;
  }
}

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasSeen, setHasSeen] = useState<boolean>(readSeen);

  // Memoized demo decision for this tour run: undefined until probed.
  const demoDecisionRef = useRef<boolean | undefined>(undefined);
  const demoEnabledRef = useRef(false);

  const disableDemo = useCallback(() => {
    if (demoEnabledRef.current) {
      demoEnabledRef.current = false;
      setAnalyticsDemoActive(false);
      analyticsService.clearAnalyticsCache();
    }
  }, []);

  const ensureDemoEnabled = useCallback(async () => {
    if (demoEnabledRef.current) return;
    if (demoDecisionRef.current === undefined) {
      demoDecisionRef.current = await probeShouldUseDemo();
    }
    if (demoDecisionRef.current) {
      demoEnabledRef.current = true;
      setAnalyticsDemoActive(true);
      analyticsService.clearAnalyticsCache();
    }
  }, []);

  const start = useCallback(() => {
    demoDecisionRef.current = undefined;
    // Kick off the probe early so the decision is ready by the analytics steps.
    void probeShouldUseDemo().then((v) => {
      if (demoDecisionRef.current === undefined) demoDecisionRef.current = v;
    });
    setStepIndex(0);
    setActive(true);
  }, []);

  const stop = useCallback(
    (completed = false) => {
      setActive(false);
      disableDemo();
      demoDecisionRef.current = undefined;
      if (completed || !hasSeen) {
        writeSeen();
        setHasSeen(true);
      }
    },
    [disableDemo, hasSeen],
  );

  const next = useCallback(async () => {
    const current = stepIndex;
    if (current >= TOUR_STEPS.length - 1) {
      stop(true);
      return;
    }
    const upcoming = TOUR_STEPS[current + 1];
    if (upcoming.analytics) {
      // Enable demo data (if the tenant is sparse) BEFORE the overlay navigates
      // to the analytics route, so the page's initial fetch gets demo data.
      await ensureDemoEnabled();
    }
    setStepIndex(current + 1);
  }, [stepIndex, stop, ensureDemoEnabled]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const value = useMemo<TutorialContextValue>(
    () => ({
      active,
      stepIndex,
      steps: TOUR_STEPS,
      start,
      stop,
      next,
      back,
      hasSeen,
    }),
    [active, stepIndex, start, stop, next, back, hasSeen],
  );

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export function useTutorial(): TutorialContextValue {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return ctx;
}
